<div class="finance-page">
  <header>
    <div>
      <h1>Finance</h1>
      <p>Track income and expenses with shared categories. Attach an invoice screenshot on expenses.</p>
    </div>
    @if (loading()) {
      <mat-spinner diameter="28"></mat-spinner>
    }
  </header>

  @if (error()) {
    <div class="error-banner">{{ error() }}</div>
  }

  @if (info()) {
    <div class="info-banner">{{ info() }}</div>
  }

  @if (summary(); as s) {
    <div class="summary-row">
      <div class="stat income">
        <span>Total income</span>
        <strong>{{ money(s.incomeTotal) }}</strong>
        <small>{{ s.incomeCount }} records</small>
      </div>
      <div class="stat expense">
        <span>Total expenses</span>
        <strong>{{ money(s.expenseTotal) }}</strong>
        <small>{{ s.expenseCount }} records</small>
      </div>
      <div class="stat net">
        <span>Net</span>
        <strong>{{ money(s.net) }}</strong>
      </div>
    </div>
  }

  <mat-tab-group>
    <mat-tab label="Income">
      <div class="tab-grid">
        <mat-card class="form-card">
          <h3>Add income</h3>
          <mat-form-field appearance="outline" class="full">
            <mat-label>Title</mat-label>
            <input matInput [(ngModel)]="incomeTitle" placeholder="Client payment" />
          </mat-form-field>
          <div class="row">
            <mat-form-field appearance="outline">
              <mat-label>Amount</mat-label>
              <input matInput type="number" [(ngModel)]="incomeAmount" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Date</mat-label>
              <input matInput type="date" [(ngModel)]="incomeDate" />
            </mat-form-field>
          </div>
          <div class="row">
            <mat-form-field appearance="outline">
              <mat-label>Category</mat-label>
              <mat-select [(ngModel)]="incomeCategory">
                @for (c of categories(); track c.id) {
                  <mat-option [value]="c.name">{{ c.name }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Source / payer</mat-label>
              <input matInput [(ngModel)]="incomeSource" />
            </mat-form-field>
          </div>
          <mat-form-field appearance="outline" class="full">
            <mat-label>Reference #</mat-label>
            <input matInput [(ngModel)]="incomeReference" />
          </mat-form-field>
          <mat-form-field appearance="outline" class="full">
            <mat-label>Notes</mat-label>
            <textarea matInput rows="2" [(ngModel)]="incomeNotes"></textarea>
          </mat-form-field>
          <button mat-flat-button color="primary" type="button" (click)="createIncome()">
            <mat-icon>add</mat-icon>
            Save income
          </button>
        </mat-card>

        <mat-card class="table-card">
          <div class="table-head">
            <h3>Income list</h3>
            <div class="csv-bar">
              <mat-form-field appearance="outline" class="csv-category">
                <mat-label>CSV category</mat-label>
                <mat-select [(ngModel)]="incomeCsvCategory">
                  <mat-option value="">All categories</mat-option>
                  @for (c of categories(); track c.id) {
                    <mat-option [value]="c.name">{{ c.name }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              <button mat-stroked-button type="button" (click)="exportCsv('income')">
                <mat-icon>download</mat-icon>
                Export CSV
              </button>
              <button mat-stroked-button type="button" (click)="downloadTemplate('income')">
                <mat-icon>description</mat-icon>
                Template
              </button>
              <label class="csv-import-btn">
                <mat-icon>upload</mat-icon>
                Import CSV
                <input type="file" accept=".csv,text/csv" hidden (change)="onImportCsv($event, 'income')" />
              </label>
            </div>
          </div>
          <table mat-table [dataSource]="incomes()" class="full">
            <ng-container matColumnDef="title">
              <th mat-header-cell *matHeaderCellDef>Title</th>
              <td mat-cell *matCellDef="let row">{{ row.title }}</td>
            </ng-container>
            <ng-container matColumnDef="category">
              <th mat-header-cell *matHeaderCellDef>Category</th>
              <td mat-cell *matCellDef="let row">{{ row.category }}</td>
            </ng-container>
            <ng-container matColumnDef="source">
              <th mat-header-cell *matHeaderCellDef>Source</th>
              <td mat-cell *matCellDef="let row">{{ row.source || '—' }}</td>
            </ng-container>
            <ng-container matColumnDef="date">
              <th mat-header-cell *matHeaderCellDef>Date</th>
              <td mat-cell *matCellDef="let row">{{ row.incomeDate }}</td>
            </ng-container>
            <ng-container matColumnDef="amount">
              <th mat-header-cell *matHeaderCellDef>Amount</th>
              <td mat-cell *matCellDef="let row">{{ money(row.amount, row.currency) }}</td>
            </ng-container>
            <ng-container matColumnDef="settled">
              <th mat-header-cell *matHeaderCellDef>Settled</th>
              <td mat-cell *matCellDef="let row">
                @if (row.isSettled) {
                  <span class="badge settled">Settled</span>
                } @else {
                  —
                }
              </td>
            </ng-container>
            <ng-container matColumnDef="actions">
              <th mat-header-cell *matHeaderCellDef></th>
              <td mat-cell *matCellDef="let row">
                <button mat-icon-button type="button" (click)="deleteIncome(row)" aria-label="Delete">
                  <mat-icon>delete</mat-icon>
                </button>
              </td>
            </ng-container>
            <tr mat-header-row *matHeaderRowDef="incomeColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: incomeColumns"></tr>
          </table>
          @if (!incomes().length) {
            <p class="empty">No income records yet.</p>
          }
        </mat-card>
      </div>
    </mat-tab>

    <mat-tab label="Expense">
      <div class="tab-grid">
        <mat-card class="form-card">
          <h3>Add expense</h3>
          <mat-form-field appearance="outline" class="full">
            <mat-label>Title</mat-label>
            <input matInput [(ngModel)]="expenseTitle" placeholder="Office supplies" />
          </mat-form-field>
          <div class="row">
            <mat-form-field appearance="outline">
              <mat-label>Amount</mat-label>
              <input matInput type="number" [(ngModel)]="expenseAmount" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Date</mat-label>
              <input matInput type="date" [(ngModel)]="expenseDate" />
            </mat-form-field>
          </div>
          <mat-form-field appearance="outline" class="full">
            <mat-label>Category</mat-label>
            <mat-select [(ngModel)]="expenseCategory">
              @for (c of categories(); track c.id) {
                <mat-option [value]="c.name">{{ c.name }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="outline" class="full">
            <mat-label>Description</mat-label>
            <textarea matInput rows="2" [(ngModel)]="expenseDescription"></textarea>
          </mat-form-field>

          <label class="upload-label">Invoice screenshot</label>
          <input type="file" accept="image/*,application/pdf" (change)="onExpenseFileSelected($event)" />
          @if (expensePreviewUrl) {
            <img class="preview" [src]="expensePreviewUrl" alt="Invoice preview" />
          } @else if (expenseFile) {
            <p class="file-name">{{ expenseFile.name }}</p>
          }

          <label class="settle-check">
            <mat-checkbox [(ngModel)]="settleAsIncome"></mat-checkbox>
            <span>
              Settle amount — also create a matching income entry (net zero).
            </span>
          </label>

          <button mat-flat-button color="warn" type="button" class="save-expense" (click)="createExpense()">
            <mat-icon>receipt_long</mat-icon>
            Save expense
          </button>
        </mat-card>

        <mat-card class="table-card">
          <div class="table-head">
            <h3>Expense list</h3>
            <div class="csv-bar">
              <mat-form-field appearance="outline" class="csv-category">
                <mat-label>CSV category</mat-label>
                <mat-select [(ngModel)]="expenseCsvCategory">
                  <mat-option value="">All categories</mat-option>
                  @for (c of categories(); track c.id) {
                    <mat-option [value]="c.name">{{ c.name }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              <button mat-stroked-button type="button" (click)="exportCsv('expense')">
                <mat-icon>download</mat-icon>
                Export CSV
              </button>
              <button mat-stroked-button type="button" (click)="downloadTemplate('expense')">
                <mat-icon>description</mat-icon>
                Template
              </button>
              <label class="csv-import-btn">
                <mat-icon>upload</mat-icon>
                Import CSV
                <input type="file" accept=".csv,text/csv" hidden (change)="onImportCsv($event, 'expense')" />
              </label>
            </div>
          </div>
          <table mat-table [dataSource]="expenses()" class="full">
            <ng-container matColumnDef="title">
              <th mat-header-cell *matHeaderCellDef>Title</th>
              <td mat-cell *matCellDef="let row">{{ row.title }}</td>
            </ng-container>
            <ng-container matColumnDef="category">
              <th mat-header-cell *matHeaderCellDef>Category</th>
              <td mat-cell *matCellDef="let row">{{ row.category }}</td>
            </ng-container>
            <ng-container matColumnDef="date">
              <th mat-header-cell *matHeaderCellDef>Date</th>
              <td mat-cell *matCellDef="let row">{{ row.expenseDate }}</td>
            </ng-container>
            <ng-container matColumnDef="amount">
              <th mat-header-cell *matHeaderCellDef>Amount</th>
              <td mat-cell *matCellDef="let row">{{ money(row.amount, row.currency) }}</td>
            </ng-container>
            <ng-container matColumnDef="settled">
              <th mat-header-cell *matHeaderCellDef>Settled</th>
              <td mat-cell *matCellDef="let row">
                @if (row.isSettled) {
                  <span class="badge settled">Settled</span>
                } @else {
                  —
                }
              </td>
            </ng-container>
            <ng-container matColumnDef="screenshot">
              <th mat-header-cell *matHeaderCellDef>Invoice</th>
              <td mat-cell *matCellDef="let row">
                @if (screenshotUrl(row); as url) {
                  <a [href]="url" target="_blank" rel="noopener">
                    @if (row.receiptUrl?.toLowerCase().endsWith('.pdf')) {
                      View PDF
                    } @else {
                      <img class="thumb" [src]="url" alt="Invoice" />
                    }
                  </a>
                } @else {
                  —
                }
              </td>
            </ng-container>
            <ng-container matColumnDef="actions">
              <th mat-header-cell *matHeaderCellDef></th>
              <td mat-cell *matCellDef="let row">
                <button mat-icon-button type="button" (click)="deleteExpense(row)" aria-label="Delete">
                  <mat-icon>delete</mat-icon>
                </button>
              </td>
            </ng-container>
            <tr mat-header-row *matHeaderRowDef="expenseColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: expenseColumns"></tr>
          </table>
          @if (!expenses().length) {
            <p class="empty">No expenses yet.</p>
          }
        </mat-card>
      </div>
    </mat-tab>

    <mat-tab label="Categories">
      <div class="tab-grid categories-grid">
        <mat-card class="form-card">
          <h3>Add category</h3>
          <p class="hint">Categories are shared by Income and Expense.</p>
          <mat-form-field appearance="outline" class="full">
            <mat-label>Category name</mat-label>
            <input matInput [(ngModel)]="newCategoryName" placeholder="e.g. Marketing" (keyup.enter)="createCategory()" />
          </mat-form-field>
          <button mat-flat-button color="primary" type="button" (click)="createCategory()">
            <mat-icon>playlist_add</mat-icon>
            Add category
          </button>
        </mat-card>

        <mat-card class="table-card">
          <h3>All categories</h3>
          <table mat-table [dataSource]="categories()" class="full">
            <ng-container matColumnDef="name">
              <th mat-header-cell *matHeaderCellDef>Name</th>
              <td mat-cell *matCellDef="let row">{{ row.name }}</td>
            </ng-container>
            <ng-container matColumnDef="type">
              <th mat-header-cell *matHeaderCellDef>Type</th>
              <td mat-cell *matCellDef="let row">
                @if (row.isDefault) {
                  <span class="badge default">Default</span>
                } @else {
                  <span class="badge custom">Custom</span>
                }
              </td>
            </ng-container>
            <ng-container matColumnDef="actions">
              <th mat-header-cell *matHeaderCellDef></th>
              <td mat-cell *matCellDef="let row">
                <button mat-icon-button type="button" color="warn" (click)="deleteCategory(row)" aria-label="Delete category">
                  <mat-icon>delete</mat-icon>
                </button>
              </td>
            </ng-container>
            <tr mat-header-row *matHeaderRowDef="categoryColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: categoryColumns"></tr>
          </table>
          @if (!categories().length) {
            <p class="empty">No categories yet.</p>
          }
        </mat-card>
      </div>
    </mat-tab>
  </mat-tab-group>
</div>
